import Image from 'next/image'

import { clsx } from 'clsx'

import { FC } from 'react'

import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import { IconBgPart } from '@/app/shared/icons'
import { IId } from '@/app/shared/interfaces/common.interface'
import {
  IColorsSite,
  IDescription,
  ISticker,
  ITitle,
} from '@/app/shared/interfaces/content.interface'
import { ITypeSite } from '@/app/shared/store/layout.store'
import { ContainerComponent } from '@/app/shared/ui/container'
import { TitleComponent } from '@/app/shared/ui/title'

// interface
interface IBarrierPage extends IId {
  data: IBarrierQuery
  typeSite: ITypeSite
}

export interface IBarrierQuery {
  title: ITitle
  sticker: ISticker[]
  description: IDescription
  colorsSite: IColorsSite
}

// component
const BarrierComponent: FC<Readonly<IBarrierPage>> = ({ data, typeSite, id }) => {
  const typeOnline = typeSite === 'online'

  // return
  return (
    <ContainerComponent
      className={clsx('pb-40 pt-[73px]', {
        'bg-secondary': typeOnline,
        'bg-success': !typeOnline,
      })}
    >
      <div
        id={id}
        className={
          'relative z-[1] grid items-start gap-[50px] bg-primary p-[30px] extraMobile:p-[60px_50px] laptop:grid-cols-[380px_1fr] desktop:grid-cols-[580px_1fr] large:grid-cols-[680px_1fr]'
        }
      >
        <div className={'grid gap-0 lg:gap-5 2xl:gap-[50px]'}>
          <TitleComponent
            className={'mb-auto tracking-tighter'}
            classSize={
              'text-[28px] extraMobile:text-[34px] tablet:text-[45px] desktop:text-[54px] large:text-[64px]'
            }
            style={{ color: data.colorsSite.siteColor }}
            animate
          >
            {data.title.title}
          </TitleComponent>
          <div
            className={
              'right-[18px] top-[100px] h-0 overflow-visible lg:right-0 lg:top-0 tablet:absolute laptop:relative laptop:h-[100px] desktop:h-[238px] large:h-[229px]'
            }
          >
            {data.sticker[0].image && (
              <AnimationMoveIconComponent
                className={
                  'laptop-left-0 absolute bottom-0 left-0 right-0 top-0 hidden h-[85px] w-[56px] overflow-visible tablet:left-[inherit] tablet:right-[18px] tablet:top-[88px] tablet:flex laptop:bottom-0 laptop:left-0 laptop:top-0 desktop:bottom-0 desktop:top-auto desktop:h-[99px] desktop:w-[64px] large:h-[125px] large:w-[82px]'
                }
                valueMoving={20}
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
              >
                <Image
                  src={data.sticker[0].image}
                  alt={'sticker'}
                  className={'overflow-visible object-contain'}
                  fill
                  sizes={'(max-width:1028px):56px, (max-width:1600px):64px, 82px'}
                />
              </AnimationMoveIconComponent>
            )}
            {data.sticker[1].image && (
              <AnimationMoveIconComponent
                className={
                  'absolute right-[18px] top-[84px] h-[80px] w-[81px] overflow-visible tablet:right-[85px] tablet:top-[14px] tablet:h-[99px] tablet:w-[100px] laptop:right-0 laptop:top-0 laptop:rotate-0 desktop:h-[137px] desktop:w-[138px] large:h-[169px] large:w-[170px]'
                }
                rotate={-12}
                valueMoving={55}
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
              >
                <Image
                  src={data.sticker[1].image}
                  alt={'sticker'}
                  className={'overflow-visible object-contain'}
                  fill
                  sizes={
                    '(max-width:520px):81px, (max-width:1028px):100px, (max-width:1600px):138px, 170px'
                  }
                />
              </AnimationMoveIconComponent>
            )}
          </div>
        </div>
        <AnimationShowBlockComponent
          initial={{
            opacity: 0,
            x: 40,
          }}
          whileInView={{
            opacity: 1,
            x: 0,
          }}
        >
          <p className={'w-[90%] text-[18px] tablet:w-full desktop:text-[22px]'}>
            {data?.description?.description ?? ''}
          </p>
        </AnimationShowBlockComponent>
        <IconBgPart className={'absolute -top-[52px] right-0 h-[54px] w-[74px] text-primary'} />
      </div>
    </ContainerComponent>
  )
}

export default BarrierComponent
